/**
 * Hooks API pour la gestion des moyens de paiement
 *
 * Fournit une interface React pour interagir avec l'API des moyens de paiement
 * en utilisant SWR pour la gestion du cache et de la synchronisation.
 *
 * @module PaymentMethodsHooks
 * @author Équipe Brydge
 * @since 2.5.0
 */

import {useState} from 'react';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import {useSWRWithSync} from 'packages/common/hooks/useSWRWithSync';
import {
  APP_EVENTS,
  notifySubscriptionsUpdated,
} from 'packages/common/utils/appEventSync';

const fetcher = (url) => axiosInstance.get(url).then((res) => res.data);

/**
 * Hook pour récupérer les moyens de paiement d'une entreprise
 *
 * @param {string} companyId - ID de l'entreprise
 * @return {Object} Liste des moyens de paiement avec état de chargement
 */
export const usePaymentMethods = (companyId) => {
  const endpointUrl = companyId ?
    endpoints.app.subscription.paymentMethods.list(companyId) :
    null;

  const {data, error, mutate, isValidating} = useSWRWithSync(
      endpointUrl,
      fetcher,
      {
        eventTypes: APP_EVENTS.SUBSCRIPTIONS.LIST_UPDATED,
        companyId,
      },
  );

  return {
    paymentMethods: data?.success ? data.data.paymentMethods : [],
    hasActiveSubscription: data?.data?.hasActiveSubscription || false,
    isLoading: !error && !data && !!companyId,
    isValidating,
    isError: !!error,
    refresh: mutate,
  };
};

/**
 * Hook pour ajouter un moyen de paiement
 *
 * @return {Object} Fonction d'ajout et état de chargement
 */
export const useAddPaymentMethod = () => {
  const [isLoading, setIsLoading] = useState(false);

  const addPaymentMethod = async (companyId, paymentMethodId, setAsDefault = false) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
          endpoints.app.subscription.paymentMethods.add(companyId),
          {paymentMethodId, setAsDefault},
      );

      // Notification pour rafraîchir les données dans tous les onglets
      notifySubscriptionsUpdated(companyId, {action: 'add_payment_method'});

      return {success: true, data: response.data.data};
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de l\'ajout du moyen de paiement',
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {addPaymentMethod, isLoading};
};

/**
 * Hook pour supprimer un moyen de paiement
 *
 * @return {Object} Fonction de suppression et état de chargement
 */
export const useDeletePaymentMethod = () => {
  const [isLoading, setIsLoading] = useState(false);

  const deletePaymentMethod = async (companyId, paymentMethodId) => {
    setIsLoading(true);
    try {
      await axiosInstance.delete(
          endpoints.app.subscription.paymentMethods.delete(companyId, paymentMethodId),
      );

      // Notification pour rafraîchir les données dans tous les onglets
      notifySubscriptionsUpdated(companyId, {action: 'delete_payment_method'});

      return {success: true};
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la suppression',
        errorCode: error.response?.data?.errorType,
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {deletePaymentMethod, isLoading};
};

/**
 * Hook pour définir un moyen de paiement par défaut
 *
 * @return {Object} Fonction de mise à jour et état de chargement
 */
export const useSetDefaultPaymentMethod = () => {
  const [isLoading, setIsLoading] = useState(false);

  const setDefaultPaymentMethod = async (companyId, paymentMethodId) => {
    setIsLoading(true);
    try {
      await axiosInstance.put(
          endpoints.app.subscription.paymentMethods.setDefault(companyId, paymentMethodId),
      );

      // Notification pour rafraîchir les données dans tous les onglets
      notifySubscriptionsUpdated(companyId, {action: 'set_default_payment_method'});

      return {success: true};
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la mise à jour',
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {setDefaultPaymentMethod, isLoading};
};
